import { escapeHtml } from '../inventoree/format'
import { durationString } from '../easeedesk/format'
import type { TableColumn } from '../ui/DataTable'
import './explained.css'

/** `DeviceProductivityModel`, `UsersProductivityModel`, `UsersPeriodProductivityModel` din public/js/productivitee.js. */
export type Column = TableColumn & { name?: string }

const DASH = '<i class="fas fa-minus font-10"></i>'

function textOrDash(value: unknown): string {
  return value ? escapeHtml(value) : DASH
}

/** "2026-09-10 08:30:00" -> "08:30", ca `moment(...).format("HH:mm")`. */
export function hhmm(value: unknown): string {
  const m = /(\d{2}):(\d{2})(?::\d{2})?$/.exec(String(value ?? ''))
  return m ? `${m[1]}:${m[2]}` : ''
}

function seconds(value: unknown, color: string): string {
  const n = Number(value)
  return n > 0 ? `<span class="${color}">${durationString(n)}</span>` : DASH
}

/**
 * EXPLAINED GAPS: cat din golurile zilei si-a explicat omul in "Your day",
 * reunit pe server (`Productivitee::withExplained`). Insigna mov, in acelasi
 * format de durata ca celelalte coloane; nimic explicat = liniuta.
 */
export function explainedChip(value: unknown): string {
  const n = Number(value)
  return n > 0 ? `<span class="od-explained-chip" title="Gaps explained in Your day">${durationString(n)}</span>` : DASH
}

const explainedColumn = (index: number): Column => ({ index, title: 'Explained gaps', name: 'explained_time', orderable: false, visible: true, html: (r) => explainedChip(r.explained_time) })

/**
 * `odDeviceName` din od_ux.js: aparatul fara nume nu lasa celula goala, ci
 * scrie "no name (#id)". (`alias` nu ajunge pe capatul asta - nici modelul
 * vechi, nici `DbalProductiviteeSource` nu-l citesc - deci ramane `Denumire`.)
 */
function deviceName(r: Record<string, unknown>): string {
  const name = String(r.Denumire ?? '').trim()
  if (name !== '') return escapeHtml(name)
  const id = String(r.device_id ?? r.ID ?? '').trim()
  return `<em class="color-light-grey">no name${id !== '' ? ` (#${escapeHtml(id)})` : ''}</em>`
}

function nameCell(r: Record<string, unknown>): string {
  return `<p>${deviceName(r)}</p>` + (Number(r.ownerId) > 0
    ? `<span class="color-light-grey"><i class="fa fa-user"></i> ${escapeHtml(r.Prenume)} ${escapeHtml(r.Nume)}</span>`
    : '<span class="color-red"><i class="fa fa-user"></i> Unassigned</span>')
}

/**
 * Randul e al OMULUI, nu al contului de masina: conturile legate de acelasi
 * proprietar de aparat (Inventoree) se aduna intr-un rand, sub numele lui
 * (`DbalProductiviteeSource::personMap`). Insigna spune pe cate masini a
 * lucrat in zi; defalcarea pe masini e in detaliul randului.
 */
export function devicesBadge(r: Record<string, unknown>): string {
  const devices = Number(r.devices)
  if (!(devices > 1)) return ''
  const accounts = Number(r.accounts)
  const title = accounts > 1 ? `${accounts} accounts of the same device owner` : `${devices} devices, one account`
  return ` <span class="label label-info" data-devices="${devices}" title="${escapeHtml(title)}"><i class="fa fa-desktop"></i> ${devices} devices</span>`
}

function userCell(r: Record<string, unknown>): string {
  return (r.starting_time
    ? '<span class="color-green2"><i class="icon-user-following font-18"></i></span> '
    : '<span class="color-red"><i class="icon-user-unfollow font-18"></i></span> ') + escapeHtml(r.username) + devicesBadge(r)
}

export function deviceColumns(): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${r.device_id}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Type', name: 'Tip', orderable: true, visible: true, width: 100, html: (r) => `<center style="cursor:default" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.device_type ?? '')}">${r.device_icon}</center>` },
    { index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true, html: nameCell },
    { index: 3, title: 'Last User Logged On', name: 'UserLogat', orderable: true, visible: true, html: (r) => textOrDash(r.logged_username) },
    { index: 4, title: 'Group', name: 'DeviceGroup', orderable: true, visible: true, html: (r) => textOrDash(r.DeviceGroup) },
    { index: 5, title: 'Productivity', name: 'productivity', orderable: true, visible: true, html: (r) => (r.productivity ? `<i class="fas fa-chart-line"></i> ${escapeHtml(r.productivity)}%` : DASH) },
    { index: 6, title: 'Productive Time', name: 'productive_time', orderable: true, visible: true, html: (r) => (Number(r.productive_time) > 0 ? durationString(Number(r.productive_time)) : DASH) },
    /* Imediat dupa timpul productiv. Ordonarea pleaca spre server dupa NUME
       (`columns[i][name]`), deci coloanele de dupa isi pot muta indicele. */
    explainedColumn(7),
    { index: 8, title: 'Starting time', name: 'starting_time', orderable: true, visible: true, html: (r) => (r.starting_time ? `<i class="far fa-clock"></i> ${hhmm(r.starting_time)}` : DASH) },
    { index: 9, title: 'Working time', name: 'working_time', orderable: true, visible: true, html: (r) => seconds(r.working_time, 'color-blue') },
    { index: 10, title: 'Active time', name: 'active_time', orderable: true, visible: true, html: (r) => seconds(r.active_time, 'color-green2') },
    { index: 11, title: 'Inactive time', name: 'inactive_time', orderable: true, visible: true, html: (r) => seconds(r.inactive_time, 'color-red') },
  ]
}

export function userColumns(period: boolean): Column[] {
  const cols: Column[] = [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${r.ID}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Username', name: 'username', orderable: true, visible: true, html: userCell },
  ]
  if (period) cols.push({ index: 2, title: 'Working day', name: 'working_day', orderable: true, visible: true, html: (r) => (r.working_day ? `<i class="fa fa-calendar"></i> ${escapeHtml(String(r.working_day).slice(0, 10))}` : DASH) })
  const n = cols.length
  cols.push(
    { index: n, title: 'Starting time', name: 'starting_time', orderable: true, visible: true, html: (r) => (r.starting_time ? `<i class="far fa-clock"></i> ${hhmm(r.starting_time)}` : DASH) },
    { index: n + 1, title: 'Working time', name: 'working_time', orderable: true, visible: true, html: (r) => seconds(r.working_time, 'color-blue') },
    { index: n + 2, title: 'Active time', name: 'active_time', orderable: true, visible: true, html: (r) => seconds(r.active_time, 'color-green2') },
    { index: n + 3, title: 'Inactive time', name: 'inactive_time', orderable: true, visible: true, html: (r) => seconds(r.inactive_time, 'color-red') },
    /* Listele oamenilor n-au coloana de timp productiv sau de neetichetate;
       explicatiile vin langa golurile pe care le explica. */
    explainedColumn(n + 4),
  )
  return cols
}
